import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '../tokens/api-tokens';
import {
  BulkUndoCancellationRequest,
  CancellationQueueCountResponse,
  CancellationQueueItem,
  PagedList
} from '../models/cancellation-queue.model';

export interface GetCancellationsParams {
  ccode?: string;
  subId?: number;
  page: number;
  pageSize: number;
  search?: string;
  cancelDateFrom?: string;
  cancelDateTo?: string;
  orderByField?: string;
  orderBy?: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root'
})
export class CancellationQueueService {
  private http = inject(HttpClient);
  private apiUrl = inject(API_BASE_URL);

  getCancellations(
    mcode: string,
    params: GetCancellationsParams
  ): Observable<PagedList<CancellationQueueItem>> {
    let httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('pageSize', String(params.pageSize));

    const ccode = params.ccode?.trim();
    if (ccode) {
      httpParams = httpParams.set('ccode', ccode);
    }

    if (params.subId != null) {
      httpParams = httpParams.set('subId', String(params.subId));
    }

    const search = params.search?.trim();
    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (params.cancelDateFrom) {
      httpParams = httpParams.set('cancelDateFrom', params.cancelDateFrom);
    }
    if (params.cancelDateTo) {
      httpParams = httpParams.set('cancelDateTo', params.cancelDateTo);
    }

    if (params.orderByField) {
      httpParams = httpParams.set('orderByField', params.orderByField);
    }
    if (params.orderBy) {
      httpParams = httpParams.set('orderBy', params.orderBy);
    }

    return this.http.get<PagedList<CancellationQueueItem>>(`${this.apiUrl}/client/${mcode}/cancellations`, {
      params: httpParams
    });
  }

  getPendingCancellations(
    mcode: string,
    params: Omit<GetCancellationsParams, 'cancelDateFrom' | 'cancelDateTo'>
  ): Observable<PagedList<CancellationQueueItem>> {
    let httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('pageSize', String(params.pageSize));

    const ccode = params.ccode?.trim();
    if (ccode) {
      httpParams = httpParams.set('ccode', ccode);
    }

    if (params.subId != null) {
      httpParams = httpParams.set('subId', String(params.subId));
    }

    const search = params.search?.trim();
    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (params.orderByField) {
      httpParams = httpParams.set('orderByField', params.orderByField);
    }
    if (params.orderBy) {
      httpParams = httpParams.set('orderBy', params.orderBy);
    }

    return this.http.get<PagedList<CancellationQueueItem>>(
      `${this.apiUrl}/client/${mcode}/cancellations/pending`,
      { params: httpParams }
    );
  }

  getPendingCancellationsCount(mcode: string, ccode?: string): Observable<CancellationQueueCountResponse> {
    let httpParams = new HttpParams();
    const trimmed = ccode?.trim();
    if (trimmed) {
      httpParams = httpParams.set('ccode', trimmed);
    }
    return this.http.get<CancellationQueueCountResponse>(
      `${this.apiUrl}/client/${mcode}/cancellations/pending/count`,
      { params: httpParams }
    );
  }

  /** POST .../cancellations/undo — 204 No Content. */
  undoCancellations(mcode: string, body: BulkUndoCancellationRequest): Observable<void> {
    return this.http
      .post<void>(`${this.apiUrl}/client/${mcode}/cancellations/undo`, body)
      .pipe(map(() => undefined));
  }

  approveCancellationRequests(mcode: string, body: BulkUndoCancellationRequest): Observable<void> {
    return this.http
      .post<void>(`${this.apiUrl}/client/${mcode}/cancellations/approve`, body)
      .pipe(map(() => undefined));
  }

  declineCancellationRequests(mcode: string, body: BulkUndoCancellationRequest): Observable<void> {
    return this.http
      .post<void>(`${this.apiUrl}/client/${mcode}/cancellations/decline`, body)
      .pipe(map(() => undefined));
  }
}
